/**
 * Frontend-Typen fuer die Warnliste (Plan 02-05). Spiegeln `backend/src/warnlisteView.ts`
 * und die Antwort von `service.listKandidatenGruppiert()` — Frontend und Backend sind
 * getrennte npm-Packages, kein gemeinsamer Import moeglich (Vorbild: das
 * Preis-Warn-Vorbildprogramm im Nachbar-Ordner, Datei `frontend/src/types.ts`).
 *
 * Deutlich simpler als das Vorbild: keine EK-/Deckungs-/Zielwert-Konzepte, Identitaet
 * ist `itemId` (Artikel-Ebene, PROJECT.md Key Decision) — kein zusammengesetzter Key.
 */

export type LeichenTyp = 'keinMandant' | 'keinBestand' | 'keinVerkauf';

export type AnzeigeStatus = 'neu' | 'offen' | 'gesnoozed' | 'behoben';

/** LIST-06 (User-Entscheidung 11.09.2026): Aktiv-Status-Filter, Default 'alle'. */
export type AktivFilter = 'alle' | 'aktiv' | 'inaktiv';

export interface KandidatRow {
  itemId: number;
  artikelNummer: string | null;
  artikelName: string | null;
  kategorieId: number | null;
  kategorieName: string | null;
  keinMandant: boolean;
  keinBestandSeit: string | null;
  keinVerkaufSeit: string | null;
  /** ECHTES Anlagedatum (live aus Plenty, LIST-01) — reine Anzeige, kein Filter. */
  plentyCreatedAt: string | null;
  /** ECHTES letztes Verkaufsdatum — reine Anzeige, kein Filter. */
  letzterVerkaufAm: string | null;
  /** Artikel-Aktiv-Status (SCAN-09/LIST-06). */
  istAktiv: boolean;
  status: 'offen' | 'behoben';
  isNew: boolean;
  firstSeenAt: string;
  /** Vom Backend abgeleiteter Anzeige-Status (warnlisteView.leiteAnzeigeStatus). */
  anzeigeStatus: AnzeigeStatus;
}

export interface KategorieGruppe {
  kategorieId: number | null;
  kategorieName: string;
  trefferzahl: number;
  kandidaten: KandidatRow[];
}

/** Antwort von `GET /programmes/artikelleichen/kandidaten`. */
export interface KandidatlisteResponse {
  kategorien: KategorieGruppe[];
  registrySnapshotStand: string | null;
  gesamtTreffer: number;
  kategorieOptionen: { kategorieId: number | null; name: string }[];
}

export interface KandidatFilterState {
  leichenTyp: LeichenTyp | '';
  kategorieId: string;
  suche: string;
  status: AnzeigeStatus | '';
  gesnoozteAnzeigen: boolean;
  /** Default 'alle' (LIST-06) — nur bei !== 'alle' als Query-Param mitgesendet. */
  aktivFilter: AktivFilter;
}

/** Welche Leichen-Typen fuer die Zeile zutreffen — Grundlage fuer die Typ-Badges. */
export function leichenTypen(row: KandidatRow): LeichenTyp[] {
  const typen: LeichenTyp[] = [];
  if (row.keinMandant) typen.push('keinMandant');
  if (row.keinBestandSeit !== null) typen.push('keinBestand');
  if (row.keinVerkaufSeit !== null) typen.push('keinVerkauf');
  return typen;
}

// =====================================================================
// Aktionen — Deaktivieren/Loeschen (ACT-02/05, Plan 03-04). Spiegeln
// backend/src/loeschQueueService.ts::listeBatches (Plan 03-03).
// =====================================================================

/** Ein Lösch-/Deaktivier-Batch mit Fortschritt je Zeilen-Status. */
export interface BatchStatus {
  batchId: string;
  aktion: 'deaktivieren' | 'loeschen';
  gesamt: number;
  offen: number;
  erledigt: number;
  uebersprungen: number;
  fehlgeschlagen: number;
  ausgeloestAm: string | null;
  fortsetzbar: boolean;
}

/** Antwort von `GET /programmes/artikelleichen/aktionen/status`. */
export interface AktionenStatusResponse {
  batches: BatchStatus[];
}
